import tw from 'twin.macro';

import { useTranslation } from 'react-i18next';

import { ProfileCardDataType } from '@/containers/IntervieweeCardContainer/types';

import Status from '../Status';
import ProfileHighlightCount from './ProfileHighlightCount';
import ProfileInfoBox from './ProfileInfoBox';
import ProfileSection from './ProfileSection';
import { RerecordingStatusBadge } from '../RerecordingStatusBadge';
import { RerecordingStatus } from '@/libs/graphql';

const ProfileCard = ({
  refId,
  refUrl,
  intervieweeName,
  intervieweeEmail,
  statusText,
  statusStyle,
  infoBoxData,
  highlightCount,
  rerecordingStatus,
}: ProfileCardDataType) => {
  const { t } = useTranslation();
  const hasRerecordingStatus = rerecordingStatus !== RerecordingStatus.None;
  const hasHighlightCount = highlightCount != null && highlightCount >= 0;

  return (
    <div tw="w-full grid grid-cols-[1fr] gap-y-6">
      <div tw="w-full grid gap-y-3 sm:grid-cols-[1fr] md:grid-cols-[1fr, 150px] md:gap-y-0 gap-x-2 items-center">
        <ProfileSection
          refId={refId}
          intervieweeName={intervieweeName}
          intervieweeEmail={intervieweeEmail}
        />
        <div
          css={[
            tw`w-full flex gap-2`,
            // Default: flex-row
            tw`flex-row`,
            // On md and up: vertical flex (single column)
            tw`md:flex-col`,
            // If re-recording status exists, use grid layout for better spacing
            hasRerecordingStatus && tw`grid grid-cols-2 md:grid-cols-1`,
          ]}
        >
          {hasHighlightCount && <ProfileHighlightCount highlightCount={highlightCount} />}
          <Status statusStyle={statusStyle} statusText={statusText} />
          {hasRerecordingStatus && (
            <RerecordingStatusBadge status={rerecordingStatus!} showIcon size="md" />
          )}
        </div>
      </div>
      {refUrl && (
        <div>
          <p tw="font-medium">
            {t(['intervieweeProfiles.refUrl.viewerLabel', 'intervieweeProfiles.refUrl.label'])}:
          </p>
          <a tw="underline break-all" href={`${refUrl}`} target="_blank" rel="noreferrer noopener">
            {refUrl}
          </a>
        </div>
      )}
      <div
        tw="grid w-full items-start gap-x-2 gap-y-3 grid-cols-[1fr] sm:grid-cols-[repeat(2, 1fr)] lg:[grid-template-columns:repeat(var(--cols),1fr)]"
        style={
          {
            '--cols': infoBoxData?.length ?? 1,
          } as React.CSSProperties
        }
      >
        {infoBoxData.map(
          (data, index) =>
            data.shouldDisplay && (
              <ProfileInfoBox key={index} label={data.label} value={data.value} />
            ),
        )}
      </div>
    </div>
  );
};

export default ProfileCard;
